import { ReadableRows } from './data-readable';
import { useState, type ReactNode, type RefObject } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { formatCount } from '@/i18n/format';
import { useIntlLocale } from '../../intl-locale';
import appDataCopy from '@/i18n/ns/app-data';
import appLifecycleCopy from '@/i18n/ns/app-lifecycle';
import appOperationsCopy from '@/i18n/ns/app-operations';
import {
  canUndo,
  displayCell,
  type DataSource,
  type DataTable,
  type KeptHistory,
  type PendingRemoval,
  type RowChange,
  type RowHistoryState,
  type RowsState,
} from './data-model';
import type { RestorePoint } from '../lifecycle/restore-dialogs';
import { useMenuBehavior } from '../menu-behavior';
import './data-parts.css';
import '../lifecycle/operation-cards.css';
import { dataImageOwner, rowHistoryImageOwner } from '../media/media-model';
import { MediaCaptureLine, ObservedImageBlock, type RenderImage } from '../media/media-parts';
import { Button } from '@/components/ui/button';
import { OriginTag, StatusPill } from '@/components/ui/kit';

type FormatAt = (iso: string) => string;

export function TablesList({
  tables,
  formatAt,
  canExport,
  canRestore,
  onExport,
  onRestoreToTime,
  onViewFiles,
  renderRows,
}: {
  tables: readonly DataTable[];
  formatAt: FormatAt;
  canExport: boolean;
  canRestore: boolean;
  onExport: (tableId: string) => void;
  onRestoreToTime: (tableId: string) => void;
  onViewFiles?: (tableId: string) => void;
  /** Each table owns its own grid: the caller returns that table's rows (a `RowsTable`). */
  renderRows?: (table: DataTable) => ReactNode;
}) {
  const copy = useNamespaceCopy(appDataCopy);
  const locale = useIntlLocale();
  return (
    <section className="pc-data-section" data-testid="data-tables">
      <header>
        <h3>{copy.tablesHeading}</h3>
        <span>{copy.liveNote}</span>
      </header>
      <ul>
        {tables.map((table) => (
          <li key={table.id} data-testid="data-table" data-kind={table.kind}>
            <div className="pc-data-table-head">
              <div>
                <strong>{table.name}</strong>
                {table.entries !== undefined && (
                  <small>
                    {table.kind === 'files'
                      ? formatCopy(copy.filesMeta, {
                          count: String(table.entries),
                          size: table.size ?? copy.absent,
                        })
                      : table.lastChange
                        ? formatCopy(copy.tableMeta, {
                            count: String(table.entries),
                            at: formatAt(table.lastChange.at),
                            who: table.lastChange.by,
                          })
                        : formatCopy(copy.tableMetaNoChange, { count: String(table.entries) })}
                  </small>
                )}
              </div>
              {table.kind === 'table' && (
                <span className="pc-data-actions">
                  {table.fields !== undefined && (
                    <span className="pc-data-muted">
                      {formatCount(copy.fieldsNoun, table.fields, locale)}
                    </span>
                  )}
                  <TableActions
                    canExport={canExport}
                    canRestore={canRestore}
                    onExport={() => onExport(table.id)}
                    onRestoreToTime={() => onRestoreToTime(table.id)}
                  />
                </span>
              )}
              {table.kind === 'files' && onViewFiles && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="data-view-files"
                  onClick={() => onViewFiles(table.id)}
                >
                  {copy.viewFiles}
                </Button>
              )}
            </div>
            {table.kind === 'table' && renderRows?.(table)}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Inline on wide screens, behind one ⋯ button on phones: actions never wrap. */
function TableActions(props: {
  canExport: boolean;
  canRestore: boolean;
  onExport: () => void;
  onRestoreToTime: () => void;
}) {
  const copy = useNamespaceCopy(appDataCopy);
  const [open, setOpen] = useState(false);
  const menu = useMenuBehavior(open, () => setOpen(false));
  if (!props.canExport && !props.canRestore) return null;
  return (
    <>
      <span className="pc-data-inline-actions">
        {props.canExport && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="data-export"
            onClick={() => props.onExport()}
          >
            {copy.exportCsv}
          </Button>
        )}
        {props.canRestore && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="data-restore-time"
            onClick={() => props.onRestoreToTime()}
          >
            {copy.restoreToTime}
          </Button>
        )}
      </span>
      <span
        className="pc-data-more"
        ref={menu.root as RefObject<HTMLSpanElement>}
        onKeyDown={menu.onKeyDown}
      >
        <Button
          ref={menu.trigger}
          type="button"
          variant="outline"
          size="sm"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={copy.moreActions}
          data-testid="data-more"
          onClick={() => setOpen((value) => !value)}
        >
          ⋯
        </Button>
        {open && (
          <div role="menu" className="pc-data-more-menu">
            {props.canExport && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  props.onExport();
                }}
              >
                {copy.exportCsv}
              </button>
            )}
            {props.canRestore && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  props.onRestoreToTime();
                }}
              >
                {copy.restoreToTime}
              </button>
            )}
          </div>
        )}
      </span>
    </>
  );
}

/** Rows with a "Row history" link per row; absent values show a dash, never zero. */
export function RowsTable({
  state,
  onRowHistory,
  onMore,
  onRetry,
  renderImage,
  ownerScope,
}: {
  state: RowsState;
  /** The table's id: part of each thumbnail's viewer identity. */
  ownerScope?: string;
  /** APP's shared image primitive; an image column shows a dash without it. */
  renderImage?: RenderImage;
  /** Absent: row history is not available for this Data, and a row is not a control. */
  onRowHistory?: (rowId: string) => void;
  onMore: () => void;
  onRetry: () => void;
}) {
  const copy = useNamespaceCopy(appDataCopy);
  if (state.status === 'loading')
    return (
      <p data-testid="rows-loading" aria-busy="true">
        {copy.loading}
      </p>
    );
  if (state.status === 'failed') {
    return (
      <div role="alert" data-testid="rows-failed" className="pc-data-failed">
        <p>{copy.rowsFailed}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => onRetry()}>
          {copy.retry}
        </Button>
      </div>
    );
  }
  if (state.rows.length === 0) return <p data-testid="rows-empty">{copy.noEntries}</p>;
  // A read that declares its column types (the native Data read) is shown readable first; anything else stays a table.
  if (state.columns.length > 0 && state.columns.every((column) => column.type))
    return <ReadableRows state={state} onRowHistory={onRowHistory} onMore={onMore} />;
  return (
    <div
      className="pc-data-rows"
      data-has-image={state.columns.some((column) => column.image) ? 'true' : undefined}
    >
      <table>
        <thead>
          <tr>
            {state.columns.map((column) => (
              <th key={column.key} scope="col" data-numeric={column.numeric ? 'true' : undefined}>
                {column.label}
              </th>
            ))}
            {onRowHistory && <th />}
          </tr>
        </thead>
        <tbody>
          {state.rows.map((row) => (
            <tr
              key={row.id}
              data-testid="data-row"
              onClick={onRowHistory ? () => onRowHistory(row.id) : undefined}
            >
              {state.columns.map((column) => (
                <td
                  key={column.key}
                  data-label={column.label}
                  data-numeric={column.numeric ? 'true' : undefined}
                  data-lead={column.lead ? 'true' : undefined}
                  data-image={column.image ? 'true' : undefined}
                >
                  {column.image ? (
                    <span
                      data-testid="data-thumb"
                      onClick={(event) => {
                        // The thumbnail's own zoom button must not also open the row's history.
                        if ((event.target as HTMLElement).closest('button'))
                          event.stopPropagation();
                      }}
                    >
                      {renderImage && row.images?.[column.key] !== undefined
                        ? renderImage(
                            row.images[column.key],
                            'thumb',
                            dataImageOwner(ownerScope ?? '', row.id, column.key),
                          )
                        : copy.absent}
                    </span>
                  ) : column.pill ? (
                    // A chip is for a real value; an absent one is plain muted text.
                    displayCell(row.cells[column.key] ?? null, copy.absent) === copy.absent ? (
                      <span className="pc-data-muted" data-testid="data-absent">
                        {copy.absent}
                      </span>
                    ) : (
                      <span data-testid="data-pill">
                        <StatusPill>
                          {displayCell(row.cells[column.key] ?? null, copy.absent)}
                        </StatusPill>
                      </span>
                    )
                  ) : (
                    displayCell(row.cells[column.key] ?? null, copy.absent)
                  )}
                </td>
              ))}
              {onRowHistory && (
                <td>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="row-history-open"
                    onClick={(event) => {
                      event.stopPropagation();
                      onRowHistory?.(row.id);
                    }}
                  >
                    {copy.rowHistory}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {state.capture && (
        <MediaCaptureLine shown={state.capture.shown} total={state.capture.total} />
      )}
      {state.hasMore && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="rows-more"
          onClick={() => onMore()}
        >
          {copy.loadMore}
        </Button>
      )}
    </div>
  );
}

export function RowHistoryPopover({
  state,
  formatAt,
  canUndoRow,
  undoing,
  onUndo,
  onClose,
  renderImage,
}: {
  renderImage?: RenderImage;
  state: RowHistoryState;
  formatAt: FormatAt;
  canUndoRow: boolean;
  /** The undo token in flight, if any. */
  undoing: string | null;
  onUndo: (change: RowChange) => void;
  onClose: () => void;
}) {
  const copy = useNamespaceCopy(appDataCopy);
  const lifecycle = useNamespaceCopy(appLifecycleCopy);
  return (
    <aside
      className="pc-row-history"
      role="dialog"
      aria-label={copy.rowHistoryTitle}
      data-testid="row-history"
    >
      <header>
        <strong>{copy.rowHistoryTitle}</strong>
        <button type="button" aria-label={lifecycle.close} onClick={() => onClose()}>
          ×
        </button>
      </header>
      {state.status === 'loading' && <p aria-busy="true">{copy.loading}</p>}
      {state.status === 'failed' && <p role="alert">{copy.loadFailed}</p>}
      {state.status === 'ready' && state.changes.length === 0 && <p>{copy.rowHistoryEmpty}</p>}
      {state.status === 'ready' && (
        <ol>
          {state.changes.map((change, index) => (
            <li key={index} data-testid="row-history-change">
              <span>
                {change.kind === 'created'
                  ? formatCopy(copy.rowHistoryCreated, { who: change.by })
                  : change.image
                    ? formatCopy(copy.rowHistoryImageChanged, { field: change.field })
                    : formatCopy(copy.rowHistoryChanged, {
                        field: change.field,
                        from: displayCell(change.from, copy.absent),
                        to: displayCell(change.to, copy.absent),
                      })}
              </span>
              <small>
                {formatCopy(copy.rowHistoryBy, { who: change.by, at: formatAt(change.at) })}
              </small>
              {change.kind === 'changed' && change.image && renderImage && (
                <ObservedImageBlock
                  image={change.image}
                  owner={rowHistoryImageOwner(change)}
                  renderImage={renderImage}
                  formatAt={formatAt}
                />
              )}
              {canUndoRow &&
                (canUndo(change) ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="row-history-undo"
                    disabled={undoing === change.undoToken}
                    onClick={() => onUndo(change)}
                  >
                    {undoing === change.undoToken ? copy.undoBusy : copy.undo}
                  </Button>
                ) : (
                  <small data-testid="row-history-no-undo">{copy.undoUnavailable}</small>
                ))}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

export function SourcesList({
  sources,
  formatAt,
  onManage,
}: {
  sources: readonly DataSource[];
  formatAt: FormatAt;
  onManage: (sourceId: string) => void;
}) {
  const copy = useNamespaceCopy(appDataCopy);
  return (
    <section className="pc-data-section" data-testid="data-sources">
      <header>
        <h3>{copy.sourcesHeading}</h3>
      </header>
      {sources.length === 0 && (
        <p className="pc-data-muted" data-testid="data-sources-none">
          {copy.sourcesNone}
        </p>
      )}
      <ul>
        {sources.map((source) => (
          <li key={source.id} data-testid="data-source" data-mode={source.mode}>
            <div>
              <strong>{source.name}</strong>
              <small>
                {[
                  source.access === 'read' ? copy.sourceReadOnly : copy.sourceReadWrite,
                  formatCopy(copy.sourceAllowed, {
                    who: source.allowedBy,
                    at: formatAt(source.allowedAt),
                  }),
                  source.lastReadAt
                    ? formatCopy(copy.sourceLastRead, { at: formatAt(source.lastReadAt) })
                    : copy.sourceNeverRead,
                  source.mode === 'owner'
                    ? formatCopy(copy.sourceViaOwner, { owner: source.ownerName })
                    : copy.sourceViaViewer,
                ].join(' · ')}
              </small>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => onManage(source.id)}>
              {copy.manage}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function RestorePointsList({
  points,
  retentionDays,
  canRestore,
  onRestore,
}: {
  points: readonly RestorePoint[];
  retentionDays: number;
  canRestore: boolean;
  onRestore: (id: string) => void;
}) {
  const lifecycle = useNamespaceCopy(appLifecycleCopy);
  return (
    <section className="pc-data-section" data-testid="data-restore-points">
      <header>
        <h3>{lifecycle.restorePointsHeading}</h3>
        <span>{formatCopy(lifecycle.restorePointsKept, { days: String(retentionDays) })}</span>
      </header>
      {points.length === 0 ? (
        <p>{lifecycle.restorePointsEmpty}</p>
      ) : (
        <ul>
          {points.map((point) => (
            <li key={point.id} data-testid="data-restore-point">
              <div>
                <strong>{point.label}</strong>
                <small>
                  {formatCopy(lifecycle.restorePointCounts, {
                    entries: String(point.entries),
                    photos: String(point.photos),
                  })}
                </small>
              </div>
              {canRestore && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onRestore(point.id)}
                >
                  {lifecycle.restore}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="pc-data-muted">{lifecycle.restorePointsNote}</p>
    </section>
  );
}

export function KeptHistoryList({ items }: { items: readonly KeptHistory[] }) {
  const copy = useNamespaceCopy(appDataCopy);
  if (items.length === 0) return null;
  return (
    <section className="pc-data-section" data-testid="data-kept">
      <header>
        <h3>{copy.keptHeading}</h3>
      </header>
      <ul>
        {items.map((item) => (
          <li key={item.id}>
            {formatCopy(copy.keptSince, { name: item.name, since: item.since })}
          </li>
        ))}
      </ul>
      <p className="pc-data-muted">{copy.keptNote}</p>
    </section>
  );
}

export function PendingRemovals({
  items,
  onReview,
}: {
  items: readonly PendingRemoval[];
  onReview: (id: string) => void;
}) {
  const copy = useNamespaceCopy(appDataCopy);
  const ops = useNamespaceCopy(appOperationsCopy);
  if (items.length === 0) return null;
  return (
    <section className="pc-data-section" data-testid="data-pending">
      <header>
        <h3>{copy.pendingHeading}</h3>
      </header>
      <p className="pc-data-muted">{copy.pendingNote}</p>
      <ul>
        {items.map((item) => (
          <li key={item.id} data-testid="data-pending-item">
            <div>
              <strong>
                {formatCopy(ops.shapeDropEditor, {
                  field: item.field,
                  n: String(item.entries),
                  m: String(item.withValue),
                })}
              </strong>
              <small>
                <span data-testid="pending-origin">
                  <OriginTag label={item.origin} />
                </span>{' '}
                {item.requestedBy}
              </small>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => onReview(item.id)}>
              {copy.pendingReview}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
